import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Loader2 } from 'lucide-react'
import PopupCloseButton from 'components/PopupCloseButton'
import { Button } from 'components/ui/button'
import { cn } from 'lib/utils'
import { createTaskCN } from 'slices/taskCNSlice'
import { taskTargetFromProject, taskDueIso } from 'utils/taskTarget'
import useKeyboardInset from '../hooks/useKeyboardInset'
import { taskAssignees } from '../utils/taskAssignees'

export interface FieldTaskComposerProps {
	/** Called once the task has been created. */
	onDone: () => void
	onCancel: () => void
	/**
	 * Pre-selected project. Passed by the project record, where the answer is
	 * already known and asking again would be ceremony.
	 */
	projectId?: string
	className?: string
}

/**
 * Every field task is created at P3.
 *
 * The control was removed because the answer was always the default: a rep
 * adding a task in a car park is recording that it needs doing, not ranking it
 * against the rest of the list. Priority stays on the model and stays editable
 * in FieldTaskEditSheet, where there is a reason to change it.
 */
const DEFAULT_PRIORITY = 'P3 - Medium'

/**
 * Add a task from the field.
 *
 * **The user picks a project and nothing else structural.** `taskCNModel`
 * requires a `packageid`, a `projectid` and a `packageType`, but the project
 * knows all three — `projectAsset` is its location. Asking a phone user for
 * three foreign keys would be absurd, and the desktop modal already resolves
 * them the same way.
 *
 * From a project record even that question disappears, because the answer came
 * with the screen.
 */
export function FieldTaskComposer({
	onDone,
	onCancel,
	projectId,
	className,
}: FieldTaskComposerProps) {
	const dispatch = useDispatch()
	const keyboardInset = useKeyboardInset()

	const userId = useSelector((state: any) => state.userLogin?.userInfo?._id)
	const userName = useSelector((state: any) => state.userLogin?.userInfo?.name)
	const { entities, ids } = useSelector((state: any) => state.projects)

	const projects = useMemo(
		() => (ids || []).map((id: string) => entities[id]).filter(Boolean),
		[entities, ids],
	)

	const [name, setName] = useState('')
	const [chosenProject, setChosenProject] = useState(projectId ?? '')
	const [assignee, setAssignee] = useState(String(userId ?? ''))
	const [due, setDue] = useState('')
	const [saving, setSaving] = useState(false)
	const [error, setError] = useState<string | null>(null)

	const project = useMemo(
		() => projects.find((p: any) => p._id === chosenProject),
		[projects, chosenProject],
	)

	/**
	 * Built from the chosen project, so it cannot exist until one is chosen.
	 * Assigning to somebody with no access to the project would produce work
	 * they cannot open.
	 */
	const assignees = useMemo(
		() => taskAssignees({ project, userId, userName }),
		[project, userId, userName],
	)

	/*
	 * Keep the selection honest when the project changes.
	 *
	 * Switching projects can strip the chosen person of access, and silently
	 * sending an id that is no longer offered would file the task to somebody
	 * who cannot see it. Falls back to the creator, who is always on the list.
	 */
	useEffect(() => {
		if (assignees.length === 0) return
		if (!assignees.some(person => person.id === assignee)) {
			setAssignee(assignees[0].id)
		}
	}, [assignees, assignee])

	const submit = useCallback(async () => {
		if (!name.trim() || saving) return

		const target = taskTargetFromProject(project)
		if (!target) {
			// The project has no location, so there is nothing to file against.
			// Saying so beats a 400 from the server with no explanation.
			setError('That project has no location on it, so a task cannot be filed against it yet.')
			return
		}

		setSaving(true)
		setError(null)

		const action: any = await (dispatch as any)(
			createTaskCN({
				...target,
				userid: userId,
				// Whoever was chosen, defaulting to the person creating it.
				assignedTo: assignee || userId,
				taskName: name.trim(),
				taskPriority: DEFAULT_PRIORITY,
				dateDue: taskDueIso(due),
				hasScheduledTime: false,
			}),
		)
		setSaving(false)

		// A rejected thunk resolves, so this has to be checked — closing here
		// regardless would throw the task away and report success.
		if (action?.meta?.requestStatus === 'rejected') {
			setError(String(action.payload ?? 'Could not save this task.'))
			return
		}
		onDone()
	}, [name, saving, project, dispatch, userId, assignee, due, onDone])

	const canSave = Boolean(name.trim() && chosenProject)

	return (
		<div
			className={cn('border-t border-gray-200 bg-white', className)}
			// Lift clear of the on-screen keyboard: iOS draws it over the page
			// rather than resizing it.
			style={{
				paddingBottom: keyboardInset
					? `${keyboardInset}px`
					: 'max(1rem, env(safe-area-inset-bottom))',
			}}
		>
			<div className="space-y-3 p-4">
				<div className="flex items-start justify-between gap-2">
					<h2 className="text-sm font-semibold text-gray-900">Add a task</h2>
					{/* Does exactly what Cancel does; it is the control people reach
					    for at the top right of a form. */}
					<PopupCloseButton onClose={onCancel} ariaLabel="Close" className="-mr-2 -mt-2" />
				</div>

				<label className="block">
					<span className="text-xs font-medium text-gray-500">Task</span>
					<input
						value={name}
						onChange={e => setName(e.target.value)}
						placeholder="What needs doing?"
						disabled={saving}
						className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2"
						// 16px or larger, or iOS zooms the page on focus.
						style={{ fontSize: '16px' }}
						autoCapitalize="sentences"
					/>
				</label>

				{/* Hidden when the project came with the screen. */}
				{!projectId && (
					<label className="block">
						<span className="text-xs font-medium text-gray-500">Project</span>
						<select
							value={chosenProject}
							onChange={e => setChosenProject(e.target.value)}
							disabled={saving}
							className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2"
							style={{ fontSize: '16px' }}
						>
							<option value="">Choose a project…</option>
							{projects.map((p: any) => (
								<option key={p._id} value={p._id}>
									{p.projectName}
								</option>
							))}
						</select>
						{/* The location is not asked for: the project supplies it. */}
					</label>
				)}

				{/*
					Who it is for, which is the question a rep actually answers when
					handing work over. Between the project and the date because that
					is the order the sentence runs: what, whose, when.
				*/}
				<label className="block">
					<span className="text-xs font-medium text-gray-500">Assigned to</span>
					<select
						value={assignee}
						onChange={e => setAssignee(e.target.value)}
						disabled={saving}
						aria-label="Assigned to"
						className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2"
						style={{ fontSize: '16px' }}
					>
						{assignees.map(person => (
							<option key={person.id} value={person.id}>
								{person.name}
							</option>
						))}
					</select>
				</label>

				{/* Full width, now that it is not sharing a row with a priority
				    nobody was changing. A date input needs the room. */}
				<label className="block">
					<span className="text-xs font-medium text-gray-500">Due</span>
					<input
						type="date"
						value={due}
						onChange={e => setDue(e.target.value)}
						disabled={saving}
						aria-label="Due"
						className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2"
						style={{ fontSize: '16px' }}
					/>
				</label>

				{error && (
					<p role="alert" className="text-sm text-destructive">
						{error}
					</p>
				)}

				<div className="flex gap-2">
					<Button variant="ghost" isInput onClick={onCancel} disabled={saving} className="flex-1">
						Cancel
					</Button>
					<Button
						variant="primary"
						isInput
						onClick={submit}
						disabled={!canSave || saving}
						className="flex-1"
					>
						{saving ? (
							<>
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
								Saving…
							</>
						) : (
							'Add task'
						)}
					</Button>
				</div>
			</div>
		</div>
	)
}

export default FieldTaskComposer
